import { isSuited, tileNumber, isTerminalOrHonor, NUM_TILE_TYPES } from './tiles';
import { TileCounts } from './hand';

/** counts(각 값 0..4)를 단일 문자열 키로. 값이 한 자리라 구분자 없이 이어붙여도 유일하다.
 *  샹텐 캐시(아래 shanten())의 키 생성에 쓰인다. */
function shantenKey(counts: TileCounts): string {
  let k = '';
  for (let i = 0; i < NUM_TILE_TYPES; i++) k += counts[i];
  return k;
}

/** 표준형 샹텐: 모든 분해를 DFS로 탐색해 최소 샹텐을 구한다.
 *  공식: shanten = 8 - 2*면자 - (작두/搭子), 단 면자+부분 ≤ 5 로 제한,
 *  면자+부분이 5인데 작두(쌍)가 하나도 없으면 +1 (작두가 없으면 완성 불가).
 *  (hot path는 shanten()을 통해 메모이제이션됨; 이 함수는 단독 호출용 순수 계산.) */
export function standardShanten(counts: TileCounts): number {
  const c = counts.slice();
  let best = 8;

  function dfs(idx: number, melds: number, partials: number, pairs: number): void {
    while (idx < NUM_TILE_TYPES && c[idx] === 0) idx++;
    if (idx === NUM_TILE_TYPES) {
      let p = partials;
      if (melds + p > 5) p = 5 - melds; // 블록은 최대 5개(4면자+1작두)
      let sh = 8 - melds * 2 - p;
      if (melds + p >= 5 && pairs === 0) sh += 1; // 작두 없음 보정
      if (sh < best) best = sh;
      return;
    }

    // 각자(3장)
    if (c[idx] >= 3) {
      c[idx] -= 3; dfs(idx, melds + 1, partials, pairs); c[idx] += 3;
    }
    // 작두/대(쌍, 2장) — 부분 + 쌍 카운트
    if (c[idx] >= 2) {
      c[idx] -= 2; dfs(idx, melds, partials + 1, pairs + 1); c[idx] += 2;
    }
    // 순자(완성 면자)
    if (isSuited(idx) && tileNumber(idx) <= 7 && c[idx + 1] > 0 && c[idx + 2] > 0) {
      c[idx]--; c[idx + 1]--; c[idx + 2]--;
      dfs(idx, melds + 1, partials, pairs);
      c[idx]++; c[idx + 1]++; c[idx + 2]++;
    }
    // 양면/변짱 (i, i+1)
    if (isSuited(idx) && tileNumber(idx) <= 8 && c[idx + 1] > 0) {
      c[idx]--; c[idx + 1]--;
      dfs(idx, melds, partials + 1, pairs);
      c[idx]++; c[idx + 1]++;
    }
    // 칸짱 (i, i+2)
    if (isSuited(idx) && tileNumber(idx) <= 7 && c[idx + 2] > 0) {
      c[idx]--; c[idx + 2]--;
      dfs(idx, melds, partials + 1, pairs);
      c[idx]++; c[idx + 2]++;
    }
    // 고립패로 버림 (한 장 무시)
    c[idx]--;
    dfs(idx, melds, partials, pairs);
    c[idx]++;
  }

  dfs(0, 0, 0, 0);
  return best;
}

/** 칠대자 샹텐 = 6 - 쌍수 + max(0, 7 - 종류수). */
export function chiitoitsuShanten(counts: TileCounts): number {
  let pairs = 0;
  let kinds = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (counts[i] >= 1) kinds++;
    if (counts[i] >= 2) pairs++;
  }
  return 6 - pairs + Math.max(0, 7 - kinds);
}

/** 국사무쌍 샹텐 = 13 - 보유 요구패 종류수 - (요구패 작두 있으면 1). */
export function kokushiShanten(counts: TileCounts): number {
  let kinds = 0;
  let hasPair = false;
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (!isTerminalOrHonor(i)) continue;
    if (counts[i] >= 1) kinds++;
    if (counts[i] >= 2) hasPair = true;
  }
  return 13 - kinds - (hasPair ? 1 : 0);
}

/** 세 형태 통합 샹텐 메모이제이션 캐시.
 *  AI hot path(ukeire)가 한 수 결정마다 shanten()을 수백 번 호출하고, 한 판 안에서는
 *  같은 손패 형태(counts)가 대량으로 반복되므로 캐시 적중률이 높다.
 *  키 = counts. 샹텐은 counts만의 순수 함수(자풍/장풍 등 컨텍스트 무관)라
 *  같은 입력→같은 출력이 보장되어 엔진 순수성·결정성을 깨지 않는다(외부 부수효과 없음).
 *  긴 세션의 메모리 증가를 막기 위해 상한 도달 시 비운다. */
const SHANTEN_CACHE = new Map<string, number>();
const SHANTEN_CACHE_MAX = 1 << 17; // 형태 상한(131072)

/** 세 형태 중 최소 샹텐. -1 = 화료. (메모이제이션) */
export function shanten(counts: TileCounts): number {
  const key = shantenKey(counts);
  const cached = SHANTEN_CACHE.get(key);
  if (cached !== undefined) return cached;
  const result = Math.min(
    standardShanten(counts),
    chiitoitsuShanten(counts),
    kokushiShanten(counts),
  );
  if (SHANTEN_CACHE.size >= SHANTEN_CACHE_MAX) SHANTEN_CACHE.clear();
  SHANTEN_CACHE.set(key, result);
  return result;
}
